import { ChevronLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

/**
 * The container every employee portal page uses. There is no second one.
 *
 * Same reasoning as `AdminPage` and `ClientPage`: in the prototype each page set
 * its own container and one of them ended up wider than the rest.
 *
 * The back link is `min-h-touch` and so is everything else on these screens.
 * This is the portal used one-handed in a customer's house — ROADMAP.md §6 says
 * the phase's real job is correctly sized controls, not a different shell.
 */
export function EmployeePage({
  title,
  description,
  back,
  actions,
  children,
}: {
  title: string;
  description?: ReactNode;
  /** The one way out of a detail page. */
  back?: { to: string; label: string };
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-content px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      {back ? (
        <Link
          to={back.to}
          className="mb-3 inline-flex min-h-touch items-center gap-1 text-label text-bark-700 underline-offset-2 hover:underline"
        >
          <ChevronLeft aria-hidden="true" className="size-4" />
          {back.label}
        </Link>
      ) : null}

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-h1 text-ink-900">{title}</h1>
          {description ? <p className="mt-1 text-body text-ink-500">{description}</p> : null}
        </div>
        {actions ? <div className="flex shrink-0 flex-wrap gap-2">{actions}</div> : null}
      </div>

      {children}
    </div>
  );
}
